import { useState, useEffect } from 'react'
import {
  TextArea,
  FileUpload,
  PdfZoom,
  Icon,
  IconType,
  DownloadFileButton,
} from '../../../../components'
import { extractUuidFromFileName } from '../../../../utils/helpers'

interface Props {
  errors: any
  register: any
  campaign: any
  onFileChange?: (file: File | null) => void
  isUploading?: boolean
  uploadProgress?: number
}

const EditRequirements = ({
  errors,
  register,
  campaign,
  onFileChange,
  isUploading = false,
  uploadProgress = 0,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [tempFileUrl, setTempFileUrl] = useState<string | null>(null)

  // Create object URL for the selected file
  useEffect(() => {
    // Clean up previous URL
    if (tempFileUrl) {
      URL.revokeObjectURL(tempFileUrl)
    }

    // Create new URL for selected file
    if (selectedFile) {
      const newUrl = URL.createObjectURL(selectedFile)
      setTempFileUrl(newUrl)
    }

    if (!selectedFile) {
      setTempFileUrl(null)
    }

    // Clean up on component unmount
    return () => {
      if (tempFileUrl) {
        URL.revokeObjectURL(tempFileUrl)
      }
    }
  }, [selectedFile])

  const handleFileChange = async (file: File) => {
    setSelectedFile(file)

    // Notify parent component about file change
    if (onFileChange) {
      onFileChange(file)
    }
  }

  // Check if there's a PDF to display
  const isPdfMimeType = campaign?.requirementsMime?.includes('pdf')
  const hasPdfName = !!campaign?.requirementsName
  const hasPdfToShow = isPdfMimeType && hasPdfName

  // Check PDF URL
  const pdfUrl = campaign?.requirementsFile?.url

  return (
    <div>
      <div className='mt-10 sm:mt-0'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <div className=''>
              <h3 className='text-lg font-semibold font-normal font-sans leading-6 text-gray-900'>
                Mentions & Scenes
              </h3>
              <p className='mt-1 text-sm font-normal leading-5 text-gray-500 font-sans'>
                Anything specific creators should mention? Try to think about
                the goal of your marketing campaign.
              </p>
              <p className='mt-1 text-sm font-normal leading-5 text-gray-500 font-sans'>
                If you have any specific scenes in mind for your video, you can
                add them here.
              </p>
              <p className='mt-1 text-sm font-normal leading-5 text-gray-500 font-sans'>
                Anything else the creator should consider?
              </p>
            </div>
          </div>
          <div className='mt-5 md:col-span-2 md:mt-0'>
            <div className='md:shadow rounded-t-md sm:overflow-hidden'>
              <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                <div className='grid gap-6'>
                  <div className='col-span-3 sm:col-span-2'>
                    <TextArea
                      error={errors.requirements?.message}
                      label='Talking points'
                      placeholder='Mentions, scenes or other requirements'
                      {...register('requirements', {
                        maxLength: {
                          value: 10000,
                          message: `Your requirements text is too long`,
                        },
                      })}
                    />
                  </div>

                  <div className='col-span-3 sm:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Attach PDF document (optional)
                    </label>

                    {/* Show the newly uploaded PDF if available */}
                    {tempFileUrl && (
                      <div className='mt-4 mb-4 max-w-full'>
                        <div className='group rounded-lg'>
                          <PdfZoom
                            src={tempFileUrl}
                            border={false}
                            className='w-full'
                            iframeHeight='500px'
                          />
                          <div className='flex justify-end py-1 px-2 bg-gray-50'>
                            <div className='flex space-x-2'>
                              <DownloadFileButton
                                fileUrl={tempFileUrl}
                                fileName={selectedFile?.name || 'document.pdf'}
                              />
                              <button
                                onClick={() =>
                                  window.open(tempFileUrl, '_blank')
                                }
                                type='button'
                                className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-white p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
                              >
                                <Icon
                                  type={IconType.externalLink}
                                  className='h-4 w-4 text-primary'
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show existing PDF if available and no new file is selected */}
                    {!tempFileUrl && hasPdfToShow && pdfUrl && (
                      <div className='mt-4 mb-4 max-w-full'>
                        <div className='group rounded-lg'>
                          <PdfZoom
                            src={pdfUrl}
                            border={false}
                            className='w-full'
                            iframeHeight='500px'
                          />
                          <div className='flex justify-end py-1 px-2 bg-gray-50'>
                            <div className='flex space-x-2'>
                              <DownloadFileButton
                                fileUrl={pdfUrl}
                                fileName={extractUuidFromFileName(
                                  campaign.requirementsName,
                                )}
                              />
                              <button
                                onClick={() => window.open(pdfUrl, '_blank')}
                                type='button'
                                className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-white p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
                              >
                                <Icon
                                  type={IconType.externalLink}
                                  className='h-4 w-4 text-primary'
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!tempFileUrl && hasPdfToShow && !pdfUrl && (
                      <div className='p-4 bg-gray-50 border border-gray-200 rounded text-gray-500 mb-4'>
                        PDF file is available but the URL is not loaded yet.
                      </div>
                    )}

                    <div>
                      <FileUpload
                        fileName={selectedFile?.name || ''}
                        onChange={handleFileChange}
                      />
                      {selectedFile && !tempFileUrl && (
                        <div className='mt-2 text-sm text-gray-500'>
                          The new PDF will replace the existing one after saving
                          the campaign
                        </div>
                      )}
                      {isUploading && (
                        <div className='mt-2 text-sm text-gray-500'>
                          Uploading: {uploadProgress}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='hidden sm:block' aria-hidden='true'>
        <div className='py-5'>
          <div className='border-t border-gray-200' />
        </div>
      </div>
    </div>
  )
}

export { EditRequirements }
